/**
 * DocGenerator — Generates and updates documentation from codebase metadata.
 *
 * Key capabilities:
 *   1. Generate a full README.md from scratch based on CodebaseSnapshot
 *   2. Update existing docs by replacing "managed sections" (fenced blocks)
 *   3. Generate API docs from routes + exports
 *   4. Generate environment variable reference docs
 *   5. Generate project structure docs
 *
 * Managed sections use HTML comment fences so they survive manual edits:
 *   <!-- docguard:begin:project-structure -->
 *   ... auto-generated content ...
 *   <!-- docguard:end:project-structure -->
 */

import * as path from 'path';
import type { CodebaseSnapshot, PackageMeta, RouteEntry, ModuleInfo } from './CodebaseAnalyzer.js';
import type { DocMapping, DocFile } from './DocMapper.js';

// ─── Public types ────────────────────────────────────────────────────────────

export interface GeneratedDoc {
  /** Relative path where this doc should be written */
  file: string;
  /** Full markdown content */
  content: string;
  /** Whether this is a new file or an update to an existing one */
  action: 'create' | 'update';
  /** Which managed sections were written */
  managedSections: string[];
  /** Human-readable summary of what changed */
  changeSummary: string;
}

export interface GeneratorOptions {
  /** Generate root README.md */
  readme: boolean;
  /** Generate docs/API.md */
  apiDocs: boolean;
  /** Generate docs/ENVIRONMENT.md */
  envDocs: boolean;
  /** Generate docs/STRUCTURE.md */
  structureDocs: boolean;
  /** Update existing docs that have managed sections */
  updateManaged: boolean;
  /** Overwrite existing files (false = only update managed sections) */
  overwrite: boolean;
}

const DEFAULT_OPTIONS: GeneratorOptions = {
  readme: true,
  apiDocs: true,
  envDocs: true,
  structureDocs: true,
  updateManaged: true,
  overwrite: false,
};

// ─── Generator ───────────────────────────────────────────────────────────────

export class DocGenerator {
  private snapshot: CodebaseSnapshot;
  private mapping: DocMapping;
  private options: GeneratorOptions;

  constructor(
    snapshot: CodebaseSnapshot,
    mapping: DocMapping,
    options: Partial<GeneratorOptions> = {},
  ) {
    this.snapshot = snapshot;
    this.mapping = mapping;
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Generate all documentation updates.
   * Returns an array of docs to write — caller decides whether to persist.
   */
  generate(): GeneratedDoc[] {
    const results: GeneratedDoc[] = [];

    if (this.options.readme) {
      results.push(this.generateReadme());
    }
    if (this.options.apiDocs && this.snapshot.routes.length > 0) {
      results.push(this.generateApiDocs());
    }
    if (this.options.envDocs && this.snapshot.envVars.length > 0) {
      results.push(this.generateEnvDocs());
    }
    if (this.options.structureDocs) {
      results.push(this.generateStructureDocs());
    }
    if (this.options.updateManaged) {
      results.push(...this.updateManagedSections());
    }

    return results;
  }

  // ─── README ──────────────────────────────────────────────────────────────

  private generateReadme(): GeneratedDoc {
    const s = this.snapshot;
    const existingReadme = this.mapping.docs.find(
      d => d.file.toLowerCase() === 'readme.md',
    );

    // If README exists and we're not overwriting, only update managed sections
    if (existingReadme && !this.options.overwrite) {
      return this.updateDocManagedSections(existingReadme, this.buildReadmeSections());
    }

    const sections: string[] = [];

    // Header
    sections.push(`# ${s.projectName}\n`);
    if (s.packageMeta?.description) {
      sections.push(`${s.packageMeta.description}\n`);
    }

    // Badges
    const badges: string[] = [];
    if (s.framework) badges.push(`![Framework](https://img.shields.io/badge/framework-${s.framework}-blue)`);
    if (s.packageMeta?.version) badges.push(`![Version](https://img.shields.io/badge/version-${s.packageMeta.version}-green)`);
    if (badges.length > 0) sections.push(badges.join(' ') + '\n');

    // Quick start
    sections.push(this.fence('quick-start', this.buildQuickStart()));

    // Project structure
    sections.push(this.fence('project-structure', this.buildProjectStructure()));

    // Scripts
    if (s.packageMeta && Object.keys(s.packageMeta.scripts).length > 0) {
      sections.push(this.fence('scripts', this.buildScriptsTable()));
    }

    // API overview
    if (s.routes.length > 0) {
      sections.push(this.fence('api-overview', this.buildApiOverview()));
    }

    // Environment variables
    if (s.envVars.length > 0) {
      sections.push(this.fence('env-vars', this.buildEnvVarsList()));
    }

    // Stats
    sections.push(this.fence('stats', this.buildStatsSection()));

    // Footer
    sections.push(`---\n\n*Documentation auto-maintained by [VibeCheck DocGuard](https://vibecheckai.dev). Last updated: ${new Date().toISOString().split('T')[0]}*\n`);

    return {
      file: 'README.md',
      content: sections.join('\n'),
      action: existingReadme ? 'update' : 'create',
      managedSections: ['quick-start', 'project-structure', 'scripts', 'api-overview', 'env-vars', 'stats'],
      changeSummary: existingReadme ? 'Updated README.md managed sections' : 'Generated README.md from codebase analysis',
    };
  }

  // ─── API Docs ────────────────────────────────────────────────────────────

  private generateApiDocs(): GeneratedDoc {
    const routes = this.snapshot.routes;
    const lines: string[] = [];

    lines.push('# API Reference\n');
    lines.push(`> Auto-generated from codebase analysis. ${routes.length} endpoint(s) detected.\n`);

    // Group by base path
    const grouped: Record<string, RouteEntry[]> = {};
    for (const r of routes) {
      const base = '/' + (r.path.split('/')[1] || '');
      if (!grouped[base]) grouped[base] = [];
      grouped[base].push(r);
    }

    for (const [base, entries] of Object.entries(grouped).sort()) {
      lines.push(`## ${base}\n`);
      lines.push('| Method | Path | File | Line |');
      lines.push('|--------|------|------|------|');
      for (const r of entries.sort((a, b) => a.path.localeCompare(b.path))) {
        lines.push(`| \`${r.method}\` | \`${r.path}\` | \`${r.file}\` | ${r.line} |`);
      }
      lines.push('');
    }

    lines.push(`---\n*Last updated: ${new Date().toISOString().split('T')[0]}*\n`);

    return {
      file: 'docs/API.md',
      content: lines.join('\n'),
      action: this.mapping.docs.some(d => d.file === 'docs/API.md') ? 'update' : 'create',
      managedSections: [],
      changeSummary: `Generated API docs with ${routes.length} endpoints`,
    };
  }

  // ─── Environment Docs ────────────────────────────────────────────────────

  private generateEnvDocs(): GeneratedDoc {
    const vars = this.snapshot.envVars;
    const lines: string[] = [];

    lines.push('# Environment Variables\n');
    lines.push(`> ${vars.length} environment variable(s) detected in codebase.\n`);
    lines.push('| Variable | Required | Description |');
    lines.push('|----------|----------|-------------|');

    for (const v of vars) {
      const isSensitive = /SECRET|KEY|TOKEN|PASSWORD|PRIVATE/i.test(v);
      const desc = isSensitive ? '*(sensitive)* — do not commit' : '';
      lines.push(`| \`${v}\` | - | ${desc} |`);
    }

    lines.push('');
    lines.push('## Setup\n');
    lines.push('```bash');
    lines.push('cp .env.example .env');
    lines.push('# Fill in the values above');
    lines.push('```\n');
    lines.push(`---\n*Last updated: ${new Date().toISOString().split('T')[0]}*\n`);

    return {
      file: 'docs/ENVIRONMENT.md',
      content: lines.join('\n'),
      action: this.mapping.docs.some(d => d.file === 'docs/ENVIRONMENT.md') ? 'update' : 'create',
      managedSections: [],
      changeSummary: `Generated env docs with ${vars.length} variables`,
    };
  }

  // ─── Structure Docs ──────────────────────────────────────────────────────

  private generateStructureDocs(): GeneratedDoc {
    const s = this.snapshot;
    const lines: string[] = [];

    lines.push('# Project Structure\n');
    lines.push(`> ${s.stats.totalFiles} source files across ${s.directories.length} directories.\n`);

    // Directory tree
    lines.push('```');
    lines.push(`${s.projectName}/`);
    for (const dir of s.directories) {
      const filesInDir = s.modules.filter(m => m.file.startsWith(dir + '/'));
      lines.push(`  ${dir}/  (${filesInDir.length} files)`);
    }
    lines.push('```\n');

    // Key modules
    const keyModules = s.modules
      .filter(m => m.exports.length >= 3)
      .sort((a, b) => b.exports.length - a.exports.length)
      .slice(0, 20);

    if (keyModules.length > 0) {
      lines.push('## Key Modules\n');
      lines.push('| File | Exports | LOC | Description |');
      lines.push('|------|---------|-----|-------------|');
      for (const m of keyModules) {
        const topExport = m.exports[0];
        const desc = topExport?.docComment?.substring(0, 60) || '';
        lines.push(`| \`${m.file}\` | ${m.exports.length} | ${m.loc} | ${desc} |`);
      }
      lines.push('');
    }

    // Language breakdown
    lines.push('## Languages\n');
    for (const [lang, count] of Object.entries(s.stats.languages).sort((a, b) => b[1] - a[1])) {
      const pct = Math.round((count / s.stats.totalFiles) * 100);
      lines.push(`- **${lang}**: ${count} files (${pct}%)`);
    }
    lines.push('');

    lines.push(`---\n*Last updated: ${new Date().toISOString().split('T')[0]}*\n`);

    return {
      file: 'docs/STRUCTURE.md',
      content: lines.join('\n'),
      action: this.mapping.docs.some(d => d.file === 'docs/STRUCTURE.md') ? 'update' : 'create',
      managedSections: [],
      changeSummary: `Generated structure docs: ${s.stats.totalFiles} files, ${s.directories.length} dirs`,
    };
  }

  // ─── Managed section updates ─────────────────────────────────────────────

  private updateManagedSections(): GeneratedDoc[] {
    const results: GeneratedDoc[] = [];
    const sectionBuilders = this.buildReadmeSections();

    for (const doc of this.mapping.docs) {
      if (doc.managedSections.length === 0) continue;
      // Skip docs we already handle (README, API, etc.)
      if (doc.file.toLowerCase() === 'readme.md') continue;

      const updated = this.updateDocManagedSections(doc, sectionBuilders);
      if (updated.managedSections.length > 0) {
        results.push(updated);
      }
    }
    return results;
  }

  private updateDocManagedSections(
    doc: DocFile,
    sectionBuilders: Record<string, string>,
  ): GeneratedDoc {
    let content = doc.content;
    const updatedSections: string[] = [];

    for (const section of doc.managedSections) {
      const newContent = sectionBuilders[section.id];
      if (!newContent) continue;

      const beginMarker = `<!-- docguard:begin:${section.id} -->`;
      const endMarker = `<!-- docguard:end:${section.id} -->`;
      const regex = new RegExp(
        `${this.escapeRegex(beginMarker)}[\\s\\S]*?${this.escapeRegex(endMarker)}`,
      );

      const replacement = `${beginMarker}\n${newContent}\n${endMarker}`;
      if (regex.test(content)) {
        content = content.replace(regex, replacement);
        updatedSections.push(section.id);
      }
    }

    return {
      file: doc.file,
      content,
      action: 'update',
      managedSections: updatedSections,
      changeSummary: updatedSections.length > 0
        ? `Updated ${updatedSections.length} managed section(s) in ${doc.file}`
        : `No changes needed in ${doc.file}`,
    };
  }

  // ─── Section builders ────────────────────────────────────────────────────

  private buildReadmeSections(): Record<string, string> {
    return {
      'quick-start': this.buildQuickStart(),
      'project-structure': this.buildProjectStructure(),
      'scripts': this.buildScriptsTable(),
      'api-overview': this.buildApiOverview(),
      'env-vars': this.buildEnvVarsList(),
      'stats': this.buildStatsSection(),
    };
  }

  private buildQuickStart(): string {
    const s = this.snapshot;
    const lines: string[] = [];
    lines.push('## Quick Start\n');
    lines.push('```bash');

    const installCmd = s.packageManager === 'pnpm' ? 'pnpm install'
      : s.packageManager === 'yarn' ? 'yarn'
      : s.packageManager === 'bun' ? 'bun install'
      : 'npm install';

    lines.push(installCmd);

    if (s.packageMeta?.scripts?.dev) {
      const devCmd = s.packageManager === 'pnpm' ? 'pnpm dev'
        : s.packageManager === 'yarn' ? 'yarn dev'
        : s.packageManager === 'bun' ? 'bun dev'
        : 'npm run dev';
      lines.push(devCmd);
    }

    lines.push('```');
    return lines.join('\n');
  }

  private buildProjectStructure(): string {
    const s = this.snapshot;
    const lines: string[] = [];
    lines.push('## Project Structure\n');
    lines.push('```');
    for (const dir of s.directories) {
      const count = s.modules.filter(m => m.file.startsWith(dir + '/')).length;
      lines.push(`${dir}/  ${count > 0 ? `(${count} files)` : ''}`);
    }
    lines.push('```');
    return lines.join('\n');
  }

  private buildScriptsTable(): string {
    const scripts = this.snapshot.packageMeta?.scripts || {};
    const entries = Object.entries(scripts);
    if (entries.length === 0) return '';

    const lines: string[] = [];
    lines.push('## Scripts\n');
    lines.push('| Command | Description |');
    lines.push('|---------|-------------|');
    for (const [name, cmd] of entries.slice(0, 15)) {
      lines.push(`| \`${name}\` | \`${cmd.length > 60 ? cmd.substring(0, 57) + '...' : cmd}\` |`);
    }
    if (entries.length > 15) {
      lines.push(`| ... | *${entries.length - 15} more scripts* |`);
    }
    return lines.join('\n');
  }

  private buildApiOverview(): string {
    const routes = this.snapshot.routes;
    if (routes.length === 0) return '';

    const lines: string[] = [];
    lines.push('## API Overview\n');
    lines.push(`${routes.length} endpoint(s) detected:\n`);

    const byMethod: Record<string, number> = {};
    for (const r of routes) {
      byMethod[r.method] = (byMethod[r.method] || 0) + 1;
    }

    for (const [method, count] of Object.entries(byMethod).sort()) {
      lines.push(`- **${method}**: ${count} endpoint(s)`);
    }

    lines.push(`\nSee [API Reference](docs/API.md) for full details.`);
    return lines.join('\n');
  }

  private buildEnvVarsList(): string {
    const vars = this.snapshot.envVars;
    if (vars.length === 0) return '';

    const lines: string[] = [];
    lines.push('## Environment Variables\n');
    lines.push(`${vars.length} variable(s) referenced in code:\n`);

    for (const v of vars.slice(0, 20)) {
      lines.push(`- \`${v}\``);
    }
    if (vars.length > 20) {
      lines.push(`- *...and ${vars.length - 20} more*`);
    }

    lines.push(`\nSee [Environment Reference](docs/ENVIRONMENT.md) for full details.`);
    return lines.join('\n');
  }

  private buildStatsSection(): string {
    const s = this.snapshot.stats;
    const lines: string[] = [];
    lines.push('## Stats\n');
    lines.push(`| Metric | Value |`);
    lines.push(`|--------|-------|`);
    lines.push(`| Source files | ${s.totalFiles} |`);
    lines.push(`| Exports | ${s.totalExports} |`);
    lines.push(`| API routes | ${s.totalRoutes} |`);
    lines.push(`| Env variables | ${s.totalEnvVars} |`);
    lines.push(`| Doc coverage | ${this.mapping.stats.coveragePercent}% |`);
    return lines.join('\n');
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  private fence(id: string, content: string): string {
    return `<!-- docguard:begin:${id} -->\n${content}\n<!-- docguard:end:${id} -->\n`;
  }

  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
